import type { ApplicantTypeLiterals } from "../../../application/applicant";
import type { ApplicantSchemaKeys } from "../applicant-schema-keys";

export const corporateSearchApplicantIds = (
  applicantSchemaKeys: ApplicantSchemaKeys,
): string[] => {
  return [
    applicantSchemaKeys.applicantName.id,
    applicantSchemaKeys.applicantCorporateNumber.id,
    applicantSchemaKeys.applicantAddress.id,
    applicantSchemaKeys.applicantPostalCode.id,
  ];
};

export const myNumberContainedAttributes = (
  applicantSchemaKeys: ApplicantSchemaKeys,
): string[] => {
  return [
    applicantSchemaKeys.applicantBirthDate.id,
    applicantSchemaKeys.applicantAddress.id,
    applicantSchemaKeys.applicantName.id,
  ];
};

export const mojCertificateContainedAttributes = (
  applicantSchemaKeys: ApplicantSchemaKeys,
): string[] => {
  return [
    applicantSchemaKeys.applicantCorporateNumber.id,
    applicantSchemaKeys.applicantName.id,
    applicantSchemaKeys.applicantAddress.id,
    applicantSchemaKeys.applicantRepresentativeName.id,
  ];
};

export const applicantUnSupportedProperties = (
  applicantType: ApplicantTypeLiterals,
  applicantSchemaKeys: ApplicantSchemaKeys,
): ApplicantSchemaKeys[keyof ApplicantSchemaKeys]["id"][] => {
  switch (applicantType) {
    case "INDIVIDUAL":
      return [
        applicantSchemaKeys.applicantCorporateNumber.id,
        applicantSchemaKeys.applicantRepresentativeName.id,
        applicantSchemaKeys.applicantContactPersonal.id,
        applicantSchemaKeys.applicantCorporateSearch.id,
      ];
    case "CORPORATE":
      return [
        applicantSchemaKeys.applicantTradeName.id,
        applicantSchemaKeys.applicantTradeNameKana.id,
        applicantSchemaKeys.applicantBirthDate.id,
      ];
    case "UNINCORPORATED_GROUP":
      return [
        applicantSchemaKeys.applicantCorporateNumber.id,
        applicantSchemaKeys.applicantTradeName.id,
        applicantSchemaKeys.applicantTradeNameKana.id,
        applicantSchemaKeys.applicantBirthDate.id,
        applicantSchemaKeys.applicantCorporateSearch.id,
      ];
  }
};

export function toMyNumberContainAttrLabel(
  id: string,
  applicantSchemaKeys: ApplicantSchemaKeys,
): string {
  switch (id) {
    case applicantSchemaKeys.applicantName.id:
      return "名前";
    case applicantSchemaKeys.applicantAddress.id:
      return "住所 / 所在地";
    case applicantSchemaKeys.applicantBirthDate.id:
      return "生年月日";
    default:
      throw new Error(`unknown label`);
  }
}

export function toMojContainAttrLabel(
  id: string,
  applicantSchemaKeys: ApplicantSchemaKeys,
): string {
  switch (id) {
    case applicantSchemaKeys.applicantName.id:
      return "法人名";
    case applicantSchemaKeys.applicantAddress.id:
      return "住所 / 所在地";
    case applicantSchemaKeys.applicantCorporateNumber.id:
      return "法人番号";
    case applicantSchemaKeys.applicantRepresentativeName.id:
      return "法人代表者名";
    default:
      throw new Error(`unknown label`);
  }
}

export const ReservedApplicantIdHelper = {
  contains: (id: string, applicantSchemaKeys: ApplicantSchemaKeys) => {
    return Object.values(applicantSchemaKeys).some(
      (schemaKey) => schemaKey.id !== "" && schemaKey.id === id,
    );
  },
  isContainMyNumber: (id: string, applicantSchemaKeys: ApplicantSchemaKeys) => {
    return myNumberContainedAttributes(applicantSchemaKeys).some(
      (attr) => attr === id,
    );
  },
  isContainMoj: (id: string, applicantSchemaKeys: ApplicantSchemaKeys) => {
    return mojCertificateContainedAttributes(applicantSchemaKeys).some(
      (attr) => attr === id,
    );
  },
  isUnsupported: (
    id: string,
    tp: ApplicantTypeLiterals,
    applicantSchemaKeys: ApplicantSchemaKeys,
  ) => {
    return applicantUnSupportedProperties(tp, applicantSchemaKeys).some(
      (attr) => attr === id,
    );
  },
  isApplicantSectionId: (
    id: string,
    applicantSchemaKeys: ApplicantSchemaKeys,
  ) => {
    return id === applicantSchemaKeys.applicantSection.id;
  },
  isApplicantType: (id: string, applicantSchemaKeys: ApplicantSchemaKeys) => {
    return id === applicantSchemaKeys.applicantType.id;
  },
  isApplicantCorporateSearch: (
    id: string,
    applicantSchemaKeys: ApplicantSchemaKeys,
  ) => {
    return id === applicantSchemaKeys.applicantCorporateSearch.id;
  },
  isApplicantName: (id: string, applicantSchemaKeys: ApplicantSchemaKeys) => {
    return id === applicantSchemaKeys.applicantName.id;
  },
  isApplicantPostalCode: (
    id: string,
    applicantSchemaKeys: ApplicantSchemaKeys,
  ) => {
    return id === applicantSchemaKeys.applicantPostalCode.id;
  },
  isApplicantAddress: (
    id: string,
    applicantSchemaKeys: ApplicantSchemaKeys,
  ) => {
    return id === applicantSchemaKeys.applicantAddress.id;
  },
  isApplicantBirthDate: (
    id: string,
    applicantSchemaKeys: ApplicantSchemaKeys,
  ) => {
    return id === applicantSchemaKeys.applicantBirthDate.id;
  },
  isApplicantCorporateNumber: (
    id: string,
    applicantSchemaKeys: ApplicantSchemaKeys,
  ) => {
    return id === applicantSchemaKeys.applicantCorporateNumber.id;
  },
  isApplicantRepresentativeName: (
    id: string,
    applicantSchemaKeys: ApplicantSchemaKeys,
  ) => {
    return id === applicantSchemaKeys.applicantRepresentativeName.id;
  },
  isApplicantPhoneNumber: (
    id: string,
    applicantSchemaKeys: ApplicantSchemaKeys,
  ) => {
    return id === applicantSchemaKeys.applicantPhoneNumber.id;
  },
  isApplicantEmail: (id: string, applicantSchemaKeys: ApplicantSchemaKeys) => {
    return id === applicantSchemaKeys.applicantEmail.id;
  },
};
